import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import Button from "./ui/Button";
import {
  getStoredConsent,
  saveConsent,
  COOKIE_CONSENT_CHANGE_EVENT,
} from "../utils/cookieConsent";

export { useCookieConsent } from "../utils/cookieConsent";

export default function CookieBanner(): React.JSX.Element | null {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const check = () => { setVisible(getStoredConsent() === null); };
    check();
    window.addEventListener(COOKIE_CONSENT_CHANGE_EVENT, check);
    return () => { window.removeEventListener(COOKIE_CONSENT_CHANGE_EVENT, check); };
  }, []);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {visible && (
        <motion.div
          // Clear the mobile bottom tab bar (h-14 ≈ 3.5rem) on small screens so
          // the banner never sits on top of the primary nav; snug to the edge on
          // desktop (md+). Respects the iOS home-indicator safe area.
          className="fixed bottom-0 left-0 right-0 z-[9990] px-3 sm:px-4 pb-[calc(env(safe-area-inset-bottom,0px)+4.75rem)] md:pb-[calc(env(safe-area-inset-bottom,0px)+1.25rem)]"
          initial={{ y: "120%", opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: "120%", opacity: 0 }}
          transition={{ type: "spring", stiffness: 340, damping: 34, mass: 0.9 }}
          role="dialog"
          aria-label="Cookie consent"
          aria-modal="false"
        >
          <div
            className="relative mx-auto max-w-2xl overflow-hidden"
            style={{
              // Frosted-glass brand surface (translucent cream + blur).
              background: "var(--c-cream-60)",
              backdropFilter: "blur(20px) saturate(1.25)",
              WebkitBackdropFilter: "blur(20px) saturate(1.25)",
              borderRadius: "var(--r-xl)",
              border: "1px solid var(--c-cream-60)",
              boxShadow: "var(--sh-lifted), inset 0 1px 0 rgba(255,255,255,.5)",
              padding: "var(--s-5) var(--s-6)",
            }}
          >
            {/* Top teal accent */}
            <div
              className="absolute inset-x-0 top-0 h-[2px] pointer-events-none z-10"
              style={{ background: "linear-gradient(to right, transparent, var(--c-kivo-teal), transparent)" }}
            />

            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              {/* Icon + text */}
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div
                  className="flex-shrink-0 w-9 h-9 flex items-center justify-center mt-0.5"
                  style={{
                    background: "var(--c-teal-weak)",
                    borderRadius: "var(--r-md)",
                    color: "var(--c-kivo-teal)",
                  }}
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5" />
                    <path d="M8.5 8.5v.01" />
                    <path d="M16 15.5v.01" />
                    <path d="M12 12v.01" />
                  </svg>
                </div>
                <div>
                  <p style={{ fontSize: "var(--fs-small)", fontWeight: 500, color: "var(--ink)", margin: "0 0 3px" }}>
                    We use cookies
                  </p>
                  <p style={{ fontSize: "var(--fs-label)", color: "var(--ink-muted)", lineHeight: 1.55, margin: 0 }}>
                    Essential cookies keep you signed in and remember your preferences. With your consent, we also collect anonymous usage analytics sent only to our own servers — no third-party trackers.{" "}
                    <a
                      href="/cookies"
                      style={{ color: "var(--c-kivo-teal)", textDecoration: "underline", textUnderlineOffset: "2px" }}
                    >
                      Cookie Policy
                    </a>
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 w-full sm:w-auto">
                <button
                  onClick={() => { saveConsent("essential"); }}
                  className="flex-1 sm:flex-none"
                  style={{
                    fontSize: "var(--fs-label)",
                    fontWeight: 600,
                    color: "var(--ink-muted)",
                    background: "var(--c-cream-weak)",
                    border: "1px solid var(--rule)",
                    cursor: "pointer",
                    padding: "9px 14px",
                    borderRadius: "var(--r-md)",
                    transition: "color var(--t-fast) var(--ease), border-color var(--t-fast) var(--ease)",
                    whiteSpace: "nowrap",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = "var(--ink)"; e.currentTarget.style.borderColor = "var(--ink-muted)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = "var(--ink-muted)"; e.currentTarget.style.borderColor = "var(--rule)"; }}
                >
                  Essential only
                </button>
                <Button size="sm" className="flex-1 sm:flex-none" onClick={() => { saveConsent("accepted"); }}>
                  Accept all
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
